import { el, topbar } from "../lib";
import { PATHS } from "../../shared/navigation";
import { textbookUnits } from "../../shared/textbook";

export function textbookView(root: HTMLElement): void {
  document.title = "二年级上册 · 小朋友学习乐园";
  root.appendChild(topbar([{ label: "英语", href: PATHS.english }, { label: "二年级上册" }]));
  const main = el("main", "wrap main-pad");
  const modes = el("div", "textbook-modes");
  modes.setAttribute("role", "group");
  modes.setAttribute("aria-label", "播放类型");
  const modeButtons = (["audio", "video"] as const).map(mode => {
    const button = el("button", "textbook-track", mode === "audio" ? "🎧 音频" : "🎬 视频") as HTMLButtonElement;
    button.type = "button";
    button.dataset.mode = mode;
    modes.appendChild(button);
    return button;
  });
  const layout = el("div", "textbook-layout");
  const player = el("section", "textbook-player");
  const title = el("h2", "section-title", "选择一段课本音频");
  const audio = document.createElement("audio");
  audio.controls = true;
  audio.preload = "none";
  const video = document.createElement("video");
  video.controls = true;
  video.preload = "none";
  video.playsInline = true;
  video.hidden = true;
  const stage = el("div", "textbook-stage");
  stage.hidden = true;
  const welcome = el("div", "textbook-welcome");
  welcome.append(el("span", "textbook-welcome-icon", "📖"), el("h3", "", "一起看课本动画"), el("p", "", "从单元目录选一段视频，开始今天的学习"));
  const playButton = el("button", "textbook-play", "▶ 开始播放") as HTMLButtonElement;
  playButton.type = "button";
  playButton.hidden = true;
  playButton.addEventListener("click", () => {
    void video.play().catch(() => { status.textContent = "暂时无法播放，请重新选择视频重试。"; });
  });
  stage.append(video, welcome, playButton);
  const status = el("p", "hint");
  status.setAttribute("role", "status");
  player.append(title, audio, stage, status);
  const grid = el("div", "textbook-units");
  let selection = 0;
  let active: HTMLButtonElement | undefined;
  let mode: "audio" | "video" = "audio";
  function renderTracks(): void {
    grid.replaceChildren();
    for (const { unit, tracks, videos } of textbookUnits) {
      const section = el("section", "textbook-unit");
      section.appendChild(el("h3", "section-title", `Unit ${unit}`));
      const list = el("ul", "textbook-tracks");
      for (const track of mode === "audio" ? tracks : videos) {
        const li = el("li");
        const button = el("button", "textbook-track", `▶ ${track.label}`) as HTMLButtonElement;
        if (mode === "video") {
          const thumbnail = document.createElement("img");
          thumbnail.src = `/textbook-posters/2a/unit${unit}/${track.section}.jpg`;
          thumbnail.alt = "";
          thumbnail.loading = "lazy";
          thumbnail.className = "textbook-thumb";
          button.prepend(thumbnail);
          button.classList.add("textbook-video-track");
        }
        button.type = "button";
        button.setAttribute("aria-label", `播放 Unit ${unit} ${track.label}`);
        button.addEventListener("click", () => {
          const request = ++selection;
          for (const media of [audio, video]) {
            media.pause();
            media.removeAttribute("src");
            media.load();
          }
          const media = mode === "audio" ? audio : video;
          if (mode === "video") {
            welcome.hidden = true;
            video.poster = `/textbook-posters/2a/unit${unit}/${track.section}.jpg`;
            video.hidden = false;
            playButton.hidden = true;
          }
          active?.removeAttribute("aria-current");
          active = button;
          button.setAttribute("aria-current", "true");
          title.textContent = `Unit ${unit} · ${track.label}`;
          status.textContent = "正在加载…";
          void (async () => {
            try {
              const response = await fetch(`/api/sign?material=textbook&k=${encodeURIComponent(track.key)}`);
              if (!response.ok) throw new Error("签名失败");
              const { url } = await response.json() as { url: string };
              if (request !== selection || !main.isConnected) return;
              media.src = url;
              await media.play();
              if (request === selection) status.textContent = "";
            } catch {
              if (request === selection && main.isConnected) {
                status.textContent = "未能自动播放，请点击播放按钮；加载失败时可重新选择条目。";
                playButton.hidden = media !== video || !video.getAttribute("src");
              }
            }
          })();
        });
        li.appendChild(button);
        list.appendChild(li);
      }
      section.appendChild(list);
      grid.appendChild(section);
    }
  }
  const setMode = (next: "audio" | "video"): void => {
    ++selection;
    mode = next;
    active = undefined;
    for (const media of [audio, video]) {
      media.pause();
      media.removeAttribute("src");
      media.load();
    }
    audio.hidden = mode !== "audio";
    video.hidden = true;
    video.removeAttribute("poster");
    stage.hidden = mode !== "video";
    welcome.hidden = false;
    playButton.hidden = true;
    title.textContent = mode === "audio" ? "选择一段课本音频" : "选择一段课本视频";
    status.textContent = "";
    modeButtons.forEach(button => button.setAttribute("aria-pressed", String(button.dataset.mode === mode)));
    renderTracks();
  };
  modeButtons.forEach(button => button.addEventListener("click", () => setMode(button.dataset.mode as "audio" | "video")));
  video.addEventListener("playing", () => { playButton.hidden = true; status.textContent = ""; });
  video.addEventListener("pause", () => {
    if (!video.getAttribute("src") || video.hidden || video.ended) return;
    playButton.textContent = "▶ 继续观看";
    playButton.hidden = false;
  });
  video.addEventListener("ended", () => {
    playButton.textContent = "↻ 再看一遍";
    playButton.hidden = false;
  });
  for (const media of [audio, video]) media.addEventListener("error", () => {
    status.textContent = "加载失败，请重新选择这段内容重试。";
  });
  setMode("audio");
  layout.append(grid, player);
  main.append(modes, layout);
  root.appendChild(main);
}
